import { doc, getDoc, setDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * User subscription plans
 */
export const PLANS = {
  FREE: 'free',
  PRO: 'pro',
  CES_PASS: 'ces_pass',
};

/**
 * Plan limits
 */
export const PLAN_LIMITS = {
  [PLANS.FREE]: {
    maxScans: 10,
    name: 'Free',
  },
  [PLANS.PRO]: {
    maxScans: Infinity,
    name: 'Pro',
  },
  [PLANS.CES_PASS]: {
    maxScans: Infinity,
    name: 'CES Pass',
  },
};

/**
 * CES Pass dates (Jan 5 - Jan 10)
 */
export const CES_PASS_DATES = {
  start: new Date('2026-01-05T00:00:00'),
  end: new Date('2026-01-10T23:59:59'),
};

/**
 * Check if CES Pass is currently valid (during event dates)
 */
export const isCESPassValid = () => {
  const now = new Date();
  return now >= CES_PASS_DATES.start && now <= CES_PASS_DATES.end;
};

/**
 * Check if CES Pass can be purchased (available before and during event, removed after Jan 10)
 * The pass will only be valid during the event dates (Jan 5-10, 2026)
 */
export const canPurchaseCESPass = () => {
  const now = new Date();
  // Can purchase until the event ends (Jan 10, 2026)
  // After Jan 10, remove the option
  return now <= CES_PASS_DATES.end;
};

/**
 * Check if subscription is still valid (not expired)
 */
const isSubscriptionValid = (subscriptionStatus, subscriptionEndDate) => {
  // Allow 'active' or 'cancelled' subscriptions to remain valid until end date
  // 'cancelled' means user cancelled but still has access until renewal date
  if (subscriptionStatus !== 'active' && subscriptionStatus !== 'cancelled') {
    return false;
  }
  
  if (!subscriptionEndDate) {
    // If no end date but status is active or cancelled, assume it's valid (legacy subscriptions)
    return true;
  }
  
  // Convert Firestore timestamp to Date if needed
  const endDate = subscriptionEndDate?.toDate ? subscriptionEndDate.toDate() : new Date(subscriptionEndDate);
  const now = new Date();
  
  // Subscription is valid if end date hasn't passed yet
  return endDate > now;
};

/**
 * Get user subscription info
 */
export const getUserSubscription = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const subscriptionStatus = userData.subscriptionStatus || 'inactive';
      const subscriptionEndDate = userData.subscriptionEndDate || null;
      
      // Check if subscription is still valid
      const isValid = isSubscriptionValid(subscriptionStatus, subscriptionEndDate);
      
      // If subscription expired, downgrade to free
      let plan = userData.plan || PLANS.FREE;
      
      // Check if CES Pass is still valid (check subscriptionEndDate, not event dates)
      // CES Pass is valid from purchase until Jan 10, 2026 (subscriptionEndDate)
      if (plan === PLANS.CES_PASS) {
        // For CES Pass, check if subscriptionEndDate hasn't passed
        // This allows users to purchase before the event and keep the plan until Jan 10
        if (subscriptionEndDate) {
          const endDate = subscriptionEndDate?.toDate ? subscriptionEndDate.toDate() : new Date(subscriptionEndDate);
          const now = new Date();
          if (endDate <= now) {
            // CES Pass has expired (past Jan 10, 2026)
            plan = PLANS.FREE;
            updateDoc(userRef, {
              plan: PLANS.FREE,
              subscriptionStatus: 'expired',
            }).catch(err => console.warn('Failed to update expired CES Pass:', err));
          }
        } else {
          // No end date set - check if we're past the event end date
          if (!isCESPassValid() && new Date() > CES_PASS_DATES.end) {
            // Event has passed and no end date was set
            plan = PLANS.FREE;
            updateDoc(userRef, {
              plan: PLANS.FREE,
              subscriptionStatus: 'expired',
            }).catch(err => console.warn('Failed to update expired CES Pass:', err));
          }
        }
      } else if (plan === PLANS.PRO && !isValid) {
        plan = PLANS.FREE;
        // Optionally update the plan in Firestore (async, don't wait)
        updateDoc(userRef, {
          plan: PLANS.FREE,
          subscriptionStatus: 'expired',
        }).catch(err => console.warn('Failed to update expired subscription:', err));
      }
      
      return {
        plan,
        scanCount: userData.scanCount || 0,
        stripeCustomerId: userData.stripeCustomerId || null,
        stripeSubscriptionId: userData.stripeSubscriptionId || null,
        subscriptionStatus: isValid ? subscriptionStatus : 'expired',
        subscriptionEndDate: subscriptionEndDate,
        isActive: isValid,
      };
    }
    
    // Default to free plan if user doesn't exist
    return {
      plan: PLANS.FREE,
      scanCount: 0,
      stripeCustomerId: null,
      stripeSubscriptionId: null,
      subscriptionStatus: 'inactive',
      subscriptionEndDate: null,
      isActive: false,
    };
  } catch (error) {
    console.error('Error getting user subscription:', error);
    throw new Error('Failed to get subscription info');
  }
};

/**
 * Increment user scan count
 */
export const incrementScanCount = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const currentCount = userSnap.data().scanCount || 0;
      await updateDoc(userRef, {
        scanCount: currentCount + 1,
        lastScanAt: serverTimestamp(),
      });
      return currentCount + 1;
    } else {
      // Create user document if it doesn't exist
      await setDoc(userRef, {
        scanCount: 1,
        plan: PLANS.FREE,
        lastScanAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });
      return 1;
    }
  } catch (error) {
    console.error('Error incrementing scan count:', error);
    throw new Error('Failed to increment scan count');
  }
};

/**
 * Check if user can scan (hasn't reached limit)
 */
export const canUserScan = async (userId) => {
  try {
    const subscription = await getUserSubscription(userId);
    const limit = PLAN_LIMITS[subscription.plan].maxScans;
    
    if (limit === Infinity) {
      return { canScan: true, remaining: Infinity };
    }
    
    const remaining = limit - subscription.scanCount;
    return {
      canScan: remaining > 0,
      remaining: Math.max(0, remaining),
      limit,
      scanCount: subscription.scanCount,
    };
  } catch (error) {
    console.error('Error checking scan limit:', error);
    return { canScan: false, remaining: 0 };
  }
};

/**
 * Calculate subscription end date (1 month from now)
 */
const calculateSubscriptionEndDate = () => {
  const endDate = new Date();
  endDate.setMonth(endDate.getMonth() + 1);
  return endDate;
};

/**
 * Update user subscription after Stripe payment
 */
export const updateUserSubscription = async (userId, subscriptionData) => {
  try {
    console.log('💾 Updating subscription for user:', userId, 'with data:', subscriptionData);
    const userRef = doc(db, 'users', userId);
    
    // First, check if user document exists
    const userSnapBefore = await getDoc(userRef);
    if (!userSnapBefore.exists()) {
      console.warn('⚠️ User document does not exist. Creating it...');
      // Create user document with subscription data
      const initialData = {
        scanCount: 0,
        plan: subscriptionData.plan || PLANS.PRO,
        subscriptionStatus: subscriptionData.status || 'active',
        createdAt: serverTimestamp(),
      };
      
      // Calculate end date if not provided
      let endDate = subscriptionData.endDate || calculateSubscriptionEndDate();
      if (endDate instanceof Date) {
        endDate = Timestamp.fromDate(endDate);
      }
      initialData.subscriptionEndDate = endDate;
      
      if (subscriptionData.stripeSessionId) initialData.stripeSessionId = subscriptionData.stripeSessionId;
      if (subscriptionData.stripeCustomerId) initialData.stripeCustomerId = subscriptionData.stripeCustomerId;
      if (subscriptionData.stripeSubscriptionId) initialData.stripeSubscriptionId = subscriptionData.stripeSubscriptionId;
      
      await setDoc(userRef, initialData);
      console.log('✅ User document created with subscription data');
      return true;
    }
    
    // User exists, proceed with update
    console.log('📋 Current user data before update:', userSnapBefore.data());
    
    // Calculate end date if not provided (1 month from now for monthly subscriptions)
    let endDate = subscriptionData.endDate || calculateSubscriptionEndDate();
    
    // Convert Date to Firestore Timestamp if needed
    if (endDate instanceof Date) {
      endDate = Timestamp.fromDate(endDate);
    }
    
    const updateData = {
      plan: subscriptionData.plan || PLANS.PRO,
      subscriptionStatus: subscriptionData.status || 'active',
      subscriptionEndDate: endDate,
      subscriptionUpdatedAt: serverTimestamp(),
    };
    
    // Only include optional fields if provided
    if (subscriptionData.stripeCustomerId) updateData.stripeCustomerId = subscriptionData.stripeCustomerId;
    if (subscriptionData.stripeSubscriptionId) updateData.stripeSubscriptionId = subscriptionData.stripeSubscriptionId;
    if (subscriptionData.stripeSessionId) updateData.stripeSessionId = subscriptionData.stripeSessionId;
    
    console.log('💾 Update data:', updateData);
    
    // Perform the update
    try {
      await updateDoc(userRef, updateData);
      console.log('✅ Subscription update sent to Firestore');
    } catch (updateError) {
      console.error('❌ Failed to update Firestore:', updateError);
      console.error('Update error code:', updateError.code);
      console.error('Update error message:', updateError.message);
      throw updateError; // Re-throw to be caught by outer catch
    }
    
    // Wait a moment for Firestore to process the write
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Force a fresh read from server (bypass cache) to verify the update
    // Use getDocFromCache first to check cache, then getDoc to force server read
    let userSnap;
    try {
      // Try to get from server (bypass cache)
      userSnap = await getDoc(userRef);
    } catch (readError) {
      console.error('❌ Failed to read from Firestore:', readError);
      throw new Error(`Failed to verify update: ${readError.message}`);
    }
    
    if (userSnap.exists()) {
      const updatedData = userSnap.data();
      console.log('✅ Verified update - Current plan:', updatedData.plan, 'Status:', updatedData.subscriptionStatus);
      console.log('📋 Full user data:', JSON.stringify(updatedData, null, 2));
      
      // Double-check the plan was actually updated
      const expectedPlan = subscriptionData.plan || PLANS.PRO;
      if (updatedData.plan !== expectedPlan) {
        console.warn('⚠️ Plan mismatch! Expected:', expectedPlan, 'Got:', updatedData.plan);
        console.warn('⚠️ This might be a cache issue. Waiting longer and retrying...');
        
        // Wait longer and try reading again (might be eventual consistency)
        await new Promise(resolve => setTimeout(resolve, 2000));
        const retrySnap = await getDoc(userRef);
        if (retrySnap.exists()) {
          const retryData = retrySnap.data();
          console.log('🔄 Retry read - Plan:', retryData.plan, 'Status:', retryData.subscriptionStatus);
          
          if (retryData.plan !== expectedPlan) {
            console.error('❌ Plan still not updated after retry. Expected:', expectedPlan, 'Got:', retryData.plan);
            throw new Error(`Plan update verification failed. Expected ${expectedPlan} but got ${retryData.plan}. This might be a Firestore permissions issue.`);
          }
        }
      } else {
        console.log('✅ Plan update verified successfully!');
      }
    } else {
      console.warn('⚠️ User document does not exist after update');
      throw new Error('User document not found after update');
    }
    
    return true;
  } catch (error) {
    console.error('❌ Error updating subscription:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    
    // Check if it's a permissions error
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: Check Firestore security rules allow users to update their own plan field.');
    }
    
    throw new Error(`Failed to update subscription: ${error.message || 'Unknown error'}`);
  }
};

/**
 * Cancel user subscription (keeps access until end date)
 */
export const cancelUserSubscription = async (userId) => {
  try {
    console.log('🚫 Cancelling subscription for user:', userId);
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userSnap.data();
    const subscriptionEndDate = userData.subscriptionEndDate;
    
    if (!subscriptionEndDate) {
      throw new Error('No subscription end date found');
    }
    
    // Mark as cancelled but keep plan active until end date
    await updateDoc(userRef, {
      subscriptionStatus: 'cancelled',
      subscriptionCancelledAt: serverTimestamp(),
      // Keep plan as 'pro' until end date - will be downgraded automatically when expired
    });
    
    console.log('✅ Subscription cancelled. Access until:', subscriptionEndDate);
    return true;
  } catch (error) {
    console.error('❌ Error cancelling subscription:', error);
    throw new Error(`Failed to cancel subscription: ${error.message || 'Unknown error'}`);
  }
};

/**
 * Get user's scan statistics
 */
export const getUserScanStats = async (userId) => {
  try {
    const subscription = await getUserSubscription(userId);
    const limit = PLAN_LIMITS[subscription.plan].maxScans;
    
    // Format end date for display
    let endDateFormatted = null;
    if (subscription.subscriptionEndDate) {
      const endDate = subscription.subscriptionEndDate?.toDate 
        ? subscription.subscriptionEndDate.toDate() 
        : new Date(subscription.subscriptionEndDate);
      endDateFormatted = endDate.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    
    return {
      scanCount: subscription.scanCount,
      limit: limit === Infinity ? 'Unlimited' : limit,
      remaining: limit === Infinity ? 'Unlimited' : Math.max(0, limit - subscription.scanCount),
      plan: subscription.plan,
      planName: PLAN_LIMITS[subscription.plan]?.name || 'Free',
      isPro: subscription.plan === PLANS.PRO || subscription.plan === PLANS.CES_PASS,
      isCESPass: subscription.plan === PLANS.CES_PASS,
      subscriptionStatus: subscription.subscriptionStatus,
      subscriptionEndDate: subscription.subscriptionEndDate,
      subscriptionEndDateFormatted: endDateFormatted,
      isActive: subscription.isActive,
    };
  } catch (error) {
    console.error('Error getting scan stats:', error);
    throw new Error('Failed to get scan statistics');
  }
};

